// 管理画面の共通部品。

import { useEffect, useState, type ReactNode } from "react";
import type { Subscription, Role, Team } from "../lib/api";

/** サービスの表示: サービス名（契約名）。サービス名は subsystem の先頭大文字、契約名は組織設定の表示名。 */
export function serviceLabel(subsystem: string, contractName: string): string {
  return `${subsystem.charAt(0).toUpperCase()}${subsystem.slice(1)}（${contractName}）`;
}

/** ユーザーのサービス使用可否（可 / 否）の編集。サービス利用が有効なサービスだけ。可はキーを持たない（サービス利用が有効なら可）。 */
export function ServiceAccess({
  subscriptions, contractName, value, onChange,
}: { subscriptions: Subscription[]; contractName: string; value: Record<string, boolean>; onChange: (v: Record<string, boolean>) => void }) {
  return (
    <div className="checks">
      {subscriptions.filter((c) => c.subscriptionActive).map((c) => {
        const v = value[c.subsystem];
        return (
          <label key={c.subsystem}>
            {serviceLabel(c.subsystem, contractName)}
            <select
              value={v === false ? "false" : "true"}
              onChange={(e) => {
                const next = { ...value };
                if (e.target.value === "true") delete next[c.subsystem];
                else next[c.subsystem] = false;
                onChange(next);
              }}
            >
              <option value="true">可</option>
              <option value="false">不可</option>
            </select>
          </label>
        );
      })}
    </div>
  );
}

export function TeamChecks({ teams, value, onChange }: { teams: Team[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="checks">
      {teams.map((t) => (
        <label key={t.teamId}>
          <input
            type="checkbox"
            checked={value.includes(t.teamId)}
            onChange={(e) => onChange(e.target.checked ? [...value, t.teamId] : value.filter((x) => x !== t.teamId))}
          />
          {t.name}
        </label>
      ))}
    </div>
  );
}

/** ポータル操作権限（business-rule「ポータル操作権限は 3 種だけ」）。admin はユーザー単位で、チームとは独立。 */
export function RoleSelect({ value, onChange }: { value: Role; onChange: (v: Role) => void }) {
  return (
    <select style={{ width: 240 }} value={value} onChange={(e) => onChange(e.target.value as Role)}>
      <option value="member">メンバー</option>
      <option value="admin">管理者（招待・ユーザー・チームの管理ができる）</option>
    </select>
  );
}

export const roleLabel = (role: Role) => (role === "admin" ? "管理者" : "メンバー");

export const fmt = (iso: string | null | undefined) => (iso ? iso.replace("T", " ").slice(0, 16) : "—");

/** ポップアップ。背景クリック・Esc で閉じる。 */
export function Modal({ title, onClose, wide, children }: { title: string; onClose: () => void; wide?: boolean; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="modal-close" aria-label="閉じる" onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * 本人性の sub（共有プールのユーザー ID）。サービスがこの人を自分の利用者に結ぶときに使う値で、
 * 管理者が転記できるようコピーのボタンを添える。
 */
export function SubCode({ sub }: { sub: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(sub); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* 選択してコピーできる */ }
  };
  return (
    <span className="sub">
      <code>{sub}</code>
      <button type="button" className="link" onClick={() => void copy()} aria-label="sub をコピー">{copied ? "コピーしました" : "コピー"}</button>
    </span>
  );
}
